/* This file contains form elements like input fields */
import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { IconButton, InputAdornment, SvgIcon, Box } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { COLOR_GREY } from '../Material';
import { makeStyles } from '@material-ui/core/styles';
import MaskedInput from 'react-text-mask';
import PropTypes from 'prop-types';
import { UsernameIcon, PasswordIcon, HidePasswordIcon, ShowPasswordIcon, AlertTriangleIcon, LOGIN_ICON_VIEWBOX } from '../SvgIcons';
import { styledCssTextField } from '../../../styles/FormElementsStyle';
import { WatchmanTheme as theme } from '../MaterialWatchman';

const isWatchman = process.env.REACT_APP_THEME_ENV === 'watchman';

const useStyles = makeStyles(theme => ({
    errorDiv: {
        marginTop: theme.spacing(0.5),
        marginLeft: theme.spacing(2),
        fontWeight: 500,
    },
}));

export const CssTextField = styledCssTextField;

const InputIcon = ({ viewBox = LOGIN_ICON_VIEWBOX, hasFocus, color = 'primary', htmlColor = COLOR_GREY.main, ...props }) => {
    const iconColor = hasFocus ? color : 'inherit';
    return <SvgIcon viewBox={viewBox} color={iconColor} htmlColor={htmlColor} {...props} />;
};

const CodeMask = props => {
    const { inputRef, ...other } = props;

    return (
        <MaskedInput
            {...other}
            ref={ref => {
                inputRef(ref ? ref.inputElement : null);
            }}
            mask={[/\d/, /\d/, /\d/, /\d/]}
            placeholder="Enter Code"
        />
    );
};

CodeMask.propTypes = {
    inputRef: PropTypes.func.isRequired,
};

export const CodeInput = ({ setResultType, error, ...props }) => {
    return (
        <CssTextField
            error={error}
            inputProps={{
                style: {
                    textAlign: 'center',
                    letterSpacing: '8px',
                    lineHeight: '150%',
                    // A special font weight for masked codes..
                    fontWeight: '700',
                },
            }}
            InputProps={{
                inputComponent: CodeMask,
            }}
            onFocus={() => {
                if (setResultType) {
                    setResultType('');
                }
            }}
            {...props}
        />
    );
};

export const EmailInput = ({ onFocus, onBlur, error, value, adornStart, AlternateAdornIcon = null, ...props }) => {
    const [hasFocus, setFocus] = useState(false);

    const emailInputAdornment =
        !hasFocus && !value
            ? { color: COLOR_GREY.main, fill: 'none' }
            : !hasFocus && error && value
            ? { color: theme.palette.error.main, fill: 'none', height: theme.spacing(2) }
            : { color: theme.palette.text.primary, fill: 'none' };

    const startAdornment = adornStart ? (
        <InputAdornment position="start">
            <InputIcon
                viewBox={LOGIN_ICON_VIEWBOX}
                style={isWatchman ? emailInputAdornment : { fill: 'currentColor' }}
                component={error && AlternateAdornIcon ? AlertTriangleIcon : UsernameIcon}
                hasFocus={hasFocus}
            />
        </InputAdornment>
    ) : undefined;

    return (
        <CssTextField
            error={error}
            InputProps={{
                startAdornment,
            }}
            type="text"
            onFocus={() => {
                setFocus(true);
                if (onFocus) {
                    onFocus();
                }
            }}
            onBlur={() => {
                setFocus(false);
                if (onBlur) {
                    onBlur();
                }
            }}
            {...props}
        />
    );
};

export const PasswordInput = ({
    focusLostCallback,
    isInvalid,
    value,
    name,
    errors,
    showErrors = true,
    adornStart,
    adornEnd,
    BoxStyling,
    inputStyling,
    AlternateAdornIcon = null,
    ...props
}) => {
    const [type, setType] = useState('password');
    const [hasFocus, setFocus] = useState(false);
    const [hasError, setError] = useState(false);

    const onVisibilityToggle = () => {
        if (type !== 'password') {
            setType('password');
        } else {
            setType('text');
        }
    };

    const icon = type === 'password' ? ShowPasswordIcon : HidePasswordIcon;

    const inputStyles = inputStyling ? inputStyling : '';

    const passwordInputAdornment =
        !hasFocus && !value
            ? { color: COLOR_GREY.main, fill: 'none' }
            : !hasFocus && hasError && value
            ? { color: theme.palette.error.main, fill: 'none', height: theme.spacing(2) }
            : { color: theme.palette.text.primary, fill: 'none' };

    const eyeInputIcon = hasFocus || value ? { color: theme.palette.text.primary } : { color: COLOR_GREY.main };

    const startAdornment = adornStart ? (
        <InputAdornment position="start">
            <InputIcon
                viewBox={LOGIN_ICON_VIEWBOX}
                style={isWatchman ? passwordInputAdornment : { fill: 'currentColor' }}
                component={!hasFocus && hasError && value && AlternateAdornIcon ? AlternateAdornIcon : PasswordIcon}
                hasFocus={hasFocus}
            />
        </InputAdornment>
    ) : undefined;

    const endAdornment = adornEnd ? (
        <InputAdornment position="end">
            <IconButton data-cy="button_eyeicon" aria-label="toggle password visibility" onClick={onVisibilityToggle} style={eyeInputIcon} edge="end">
                <InputIcon viewBox={LOGIN_ICON_VIEWBOX} component={icon} hasFocus={hasFocus} />
            </IconButton>
        </InputAdornment>
    ) : undefined;

    return (
        <Box display="flex" flexDirection="column" id={`PasswordRoot-${name}`} className={BoxStyling ? BoxStyling : ''}>
            <CssTextField
                id={`TextFieldGetsMargin-${name}`}
                error={hasError && !hasFocus && !!value}
                isinvalid={isInvalid}
                InputProps={{
                    onFocus: () => {
                        if (focusLostCallback) {
                            // indicate globally focus not lost for element
                            focusLostCallback(false);
                        }
                        setFocus(true);
                    },
                    onBlur: () => {
                        if (focusLostCallback && !!value) {
                            //indicate globally focus is lost for element
                            focusLostCallback(true);
                        }
                        setFocus(false);
                        handleBlur(name, setError, errors);
                    },
                    startAdornment,
                    endAdornment,
                    classes: { root: inputStyles },
                }}
                value={value}
                type={type}
                name={name}
                {...props}
            />
            {!hasFocus && hasError && showErrors ? <ErrorDiv text={errors[name]} /> : null}
        </Box>
    );
};

export function ErrorDiv({ text }) {
    const classes = useStyles();
    return (
        <Typography variant="caption" color="error" className={classes.errorDiv}>
            {text}
        </Typography>
    );
}

function handleBlur(name, setError, errors) {
    if (errors && errors[name]) {
        setError(true);
    } else {
        setError(false);
    }
}

const validatedInput = props => {
    return (
        <input
            children={props.children}
            className={props.className}
            disabled={props.disabled}
            name={props.healthCardNumber}
            onBlur={props.onBlur}
            onChange={props.onChange}
            theme={props.theme}
            value={props.value}
            type={props.type}
        />
    );
};

/* styled */
export const Input = styled(validatedInput)`
    font-family: Avenir Next;
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 22px;
    letter-spacing: 0.16px;
    color: #212121;
    border: 1px solid #979797;
    border-radius: 5px;
    padding-left: 10px;
    padding-top: 11px;
    padding-right: 60px;
    padding-bottom: 10px;
    box-sizing: border-box;
    &:focus {
        outline: 0;
    }

    ${props => {
        if (props.isinvalid) {
            return css`
                border-color: #ff0000;
            `;
        } else {
            return css`
                border-color: #979797;
            `;
        }
    }}
`;

export const InputWithMarginA = styled(Input)`
    margin-top: 10px;
    width: 100%;
`;

export const PasswordInputWithMarginA = styled(PasswordInput)`
    margin-bottom: 10px;
`;

export const Select = styled.select`
    border: 1px solid #979797;
    border-radius: 5px;
    font-weight: 500;
    font-size: 16px;
    font-family: Avenir Next;
    font-style: normal;
    line-height: 22px;
    letter-spacing: 0.16px;
    color: #212121;
    padding-top: 12px;
    padding-bottom: 11px;
    padding-left: 10px;
    padding-right: 40px;
    background-color: rgba(255, 255, 255, 0.1);
    appearance: none;
    overflow: hidden;
    white-space: normal;
`;
